import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
    revealLeadKyc,
    revealStaffKyc,
} from '../../../store/actions/kycRevealActionCreator';
import { showMessage } from '../../../utils/helper';

export type RevealAction =
    'UNMASK_NUMBER' | 'UNBLUR_IMAGE' | 'UNMASK_BANK_ACCOUNT';

export const REVEAL_REASON_CODES = [
    { value: 'DOC_VERIFICATION_QC', label: 'Document verification / QC' },
    { value: 'RIDER_SUPPORT_TICKET', label: 'Rider support ticket' },
    { value: 'DUPLICATE_INVESTIGATION', label: 'Duplicate investigation' },
    { value: 'BGV_DISCREPANCY', label: 'BGV discrepancy' },
    { value: 'PAYROLL_BANK_MISMATCH', label: 'Payroll / bank mismatch' },
    { value: 'LAW_ENFORCEMENT_REQUEST', label: 'Law enforcement request' },
    { value: 'INTERNAL_AUDIT', label: 'Internal audit' },
    { value: 'DATA_CORRECTION', label: 'Data correction' },
    { value: 'OTHER', label: 'Other (describe below)' },
];

export const OTHER_REASON_MIN_LENGTH = 20;

/**
 * Human copy for each server-side denial. The server never explains itself in a way worth
 * showing verbatim, and "you do not have permission" vs "this document is approved" are very
 * different actions for the operator.
 */
const DENIAL_COPY: Record<string, string> = {
    DENIED_RBAC: 'You do not have permission to reveal KYC data.',
    DENIED_STATUS:
        'Approved documents can never be revealed. This is enforced for every role.',
    DENIED_DOC_TYPE: 'Aadhaar numbers can never be revealed.',
    DENIED_OWNERSHIP: 'This document does not belong to this rider.',
    DENIED_RATE_LIMIT:
        'Reveal limit reached. Try again later — limits reset hourly.',
    DENIED_BLUR_UNAVAILABLE:
        'This image is still being processed and cannot be shown yet.',
};

export interface RevealedValue {
    documentNo?: string | null;
    rawImageUrl?: string | null;
    /** False when the gates passed but raw disclosure is still switched off server-side. */
    rawReturned: boolean;
    expiresAt: number;
}

interface RevealSubject {
    entityType: 'USER' | 'LEAD';
    entityId: number | string;
    /**
     * Refetch the KYC payload. Called after every allowed reveal: the grant lives server-side,
     * so the GET is what turns the whole document raw - number, front image, back image - and
     * keeps one source of truth instead of patching values in from the reveal response.
     */
    onRevealed?: () => void;
}

interface KycRevealContextValue extends RevealSubject {
    /** Keyed `${documentId}:${action}`. */
    revealed: Record<string, RevealedValue>;
    requestReveal: (args: {
        documentId: number | string;
        documentType: string;
        action: RevealAction;
        /**
         * Sides to reveal. One request per side, sent together so a two-sided document costs the
         * operator a single click. Omit, or pass [null], for a single-image document.
         */
        documentKeys?: Array<string | null>;
        reasonCode: string;
        reasonText?: string;
    }) => Promise<boolean>;
    pendingKey: string | null;
}

const KycRevealContext = createContext<KycRevealContextValue | null>(null);

/**
 * Keyed by side as well as document. Without the third segment a FRONT and a BACK reveal on the
 * same Aadhaar collapse onto one entry, and both image slots read it — which is why revealing an
 * Aadhaar showed the front twice.
 */
export const revealKeyOf = (
    documentId: number | string | undefined | null,
    action: RevealAction,
    documentKey?: string | null,
) => `${documentId ?? ''}:${action}:${documentKey ?? ''}`;

/**
 * Holds reveal state for one rider or lead.
 *
 * The revealed value is kept in memory only and expires on a timer, so it disappears on
 * navigation, refresh, or after the server-side grant lapses — whichever comes first. Nothing
 * is written to storage.
 */
export const KycRevealProvider: React.FC<
    RevealSubject & { children: React.ReactNode }
> = ({ entityType, entityId, onRevealed, children }) => {
    const dispatch = useDispatch();
    const [revealed, setRevealed] = useState<Record<string, RevealedValue>>({});
    const [pendingKey, setPendingKey] = useState<string | null>(null);

    const requestReveal = useCallback<KycRevealContextValue['requestReveal']>(
        ({
            documentId,
            documentType,
            action,
            documentKeys,
            reasonCode,
            reasonText,
        }) =>
            new Promise((resolve) => {
                const sides: Array<string | null> =
                    documentKeys && documentKeys.length ? documentKeys : [null];
                const key = revealKeyOf(documentId, action, sides[0]);
                setPendingKey(key);

                const body = {
                    entityType,
                    entityId,
                    reasonCode,
                    reasonText,
                    items: sides.map((documentKey) => ({
                        documentId,
                        documentType,
                        action,
                        documentKey,
                    })),
                };

                const onSuccess = (resp: any) => {
                    setPendingKey(null);
                    // The middleware hands back the whole ServiceResponse envelope
                    // ({ status, response }), not the inner payload.
                    const results: any[] =
                        resp?.response?.results ?? resp?.results ?? [];
                    const result = results[0];
                    if (!result) {
                        showMessage(
                            resp?.status?.message ||
                                'Reveal failed, please try again',
                            'error',
                        );
                        return resolve(false);
                    }
                    if (result.outcome !== 'ALLOWED') {
                        showMessage(
                            DENIAL_COPY[result.outcome] ||
                                result.denialMessage ||
                                'Reveal was not permitted',
                            'error',
                        );
                        return resolve(false);
                    }
                    if (!result.rawReturned) {
                        // Gates passed but the dark-launch flag is still off. The attempt is
                        // audited either way, so say so rather than showing an empty field.
                        showMessage(
                            'Reveal is recorded but not yet enabled in this environment',
                            'info',
                        );
                    }
                    // Stored per side, using the side the SERVER says it served. A blank request
                    // key resolves to FRONT server-side, so trusting the requested value would
                    // file the front image under an empty key and leave the slots reading each
                    // other's entry.
                    setRevealed((prev) => {
                        const next = { ...prev };
                        results.forEach((item: any, index: number) => {
                            if (item?.outcome !== 'ALLOWED') return;
                            const served = item.documentKey ?? null;
                            const requested = sides[index] ?? null;
                            const value = {
                                documentNo: item.documentNoRevealed,
                                rawImageUrl: item.rawImageUrl,
                                rawReturned: !!item.rawReturned,
                                expiresAt:
                                    Date.now() +
                                    (item.expiresInSeconds || 0) * 1000,
                            };
                            next[revealKeyOf(documentId, action, served)] =
                                value;
                            // Also filed under what was asked for. A caller with one image slot
                            // sends no side and reads back under none, but the server answers
                            // FRONT — without this the grant lands on a key nobody reads and the
                            // image never swaps.
                            if (requested !== served) {
                                next[
                                    revealKeyOf(documentId, action, requested)
                                ] = value;
                            }
                        });
                        return next;
                    });
                    // Re-read the document through the normal API so the grant is applied
                    // consistently across every field, not just the one item revealed here.
                    onRevealed?.();
                    return resolve(true);
                };

                const onError = (err: any) => {
                    setPendingKey(null);
                    // Non-200s already surface status.message via the middleware's own
                    // snackbar, so only add one when there is nothing else to show.
                    if (!err?.status?.message) {
                        showMessage(
                            err?.message || 'Reveal failed, please try again',
                            'error',
                        );
                    }
                    resolve(false);
                };

                dispatch(
                    entityType === 'LEAD'
                        ? (revealLeadKyc(
                              entityId,
                              body,
                              onSuccess,
                              onError,
                          ) as any)
                        : (revealStaffKyc(
                              entityId,
                              body,
                              onSuccess,
                              onError,
                          ) as any),
                );
            }),
        [dispatch, entityType, entityId, onRevealed],
    );

    // Re-mask when the grant lapses.
    //
    // onRevealed() refetches WHILE the grant is live, so the payload that comes back holds the raw
    // number and the raw image URLs. The countdown only governs the in-memory overlay, so when it
    // hit zero the component fell back to props that were themselves raw — the document stayed
    // unmasked until something else refetched, and the Reveal button came back while the value on
    // screen was still in the clear. Dropping the expired entry is not enough for the same reason;
    // the server has to be asked again, now that it will answer masked.
    useEffect(() => {
        const nextExpiry = Object.values(revealed).reduce(
            (soonest, v) => (soonest === 0 || v.expiresAt < soonest ? v.expiresAt : soonest),
            0,
        );
        if (!nextExpiry) return undefined;

        const timer = setTimeout(() => {
            setRevealed((prev) => {
                const now = Date.now();
                const next: Record<string, RevealedValue> = {};
                Object.entries(prev).forEach(([k, v]) => {
                    if (v.expiresAt > now) next[k] = v;
                });
                return next;
            });
            onRevealed?.();
        }, Math.max(0, nextExpiry - Date.now()));

        return () => clearTimeout(timer);
    }, [revealed, onRevealed]);

    const value = useMemo(
        () => ({ entityType, entityId, revealed, requestReveal, pendingKey }),
        [entityType, entityId, revealed, requestReveal, pendingKey],
    );

    return (
        <KycRevealContext.Provider value={value}>
            {children}
        </KycRevealContext.Provider>
    );
};

/**
 * Null when no provider is mounted, which is how every existing screen keeps working untouched:
 * the reveal controls simply do not render.
 */
export const useKycReveal = () => useContext(KycRevealContext);
